import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Gruppe } from '../../../gruppen/classes/gruppe';
import { TierFilterSettings } from './classes/tier-filter-settings';
import { TierFilterDialogResult } from './classes/tier-filter-dialog-result';
import { TierFilter } from './classes/tier-filter';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { Haendler } from '../../../stammdaten/haendler/haendler';
import { GruppeTierEinstalldatumFilterComponent } from '../../../../components/gruppe-tier/filter/gruppe-tier-einstalldatum-filter/gruppe-tier-einstalldatum-filter.component';
import { GruppeTierEinstalldatum } from '../../../../components/gruppe-tier/filter/gruppe-tier-einstalldatum-filter/gruppe-tier-einstalldatum';
import { GruppeTierViehhaendlerFilterComponent } from '../../../../components/gruppe-tier/filter/gruppe-tier-viehhaendler-filter/gruppe-tier-viehhaendler-filter.component';
import { Betriebskontakt } from '../../../stammdaten/betriebskontakte/betriebskontakt';
import { BetriebskontaktAuswahlConfig } from '../../../stammdaten/betriebskontakte/betriebskontakt-auswahl.component';
import { StammdatenService } from '../../../stammdaten/stammdaten.service';
import { HttpParams } from '@angular/common/http';
import { formatDate } from '@angular/common';
import { forkJoin } from 'rxjs';
import { formatTierMengenLabel, TierMengenRow } from '../tier-mengen-label';

type BetriebskontaktMitAnzahl = Betriebskontakt & TierMengenRow;

@Component({
    selector: 'kiene-gruppe-tier-filter-dialog',
    templateUrl: './gruppe-tier-filter-dialog.component.html',
    styleUrls: ['./gruppe-tier-filter-dialog.component.scss'],
})
export class GruppeTierFilterDialogComponent implements OnInit {
    @ViewChild('filterEinstalldatum')
    filterEinstalldatum: GruppeTierEinstalldatumFilterComponent;
    @ViewChild('filterViehhaendler')
    filterViehhaendler: GruppeTierViehhaendlerFilterComponent;
    gruppe: Gruppe;
    gruppeIds: number[] = [];
    filterSettings: TierFilterSettings = new TierFilterSettings();
    selectedTierFilterCtrl: UntypedFormControl = new UntypedFormControl();
    info: string;
    showEinstallgewichtInfo = false;
    showAbgangsgewichtInfo = false;
    disableTierFilterSelection = false;
    disabledTierFilterOptions: TierFilter[] = [];
    showSaveFilterOption = false;
    saveFilterCtrl = new FormControl(false);
    betriebskontaktFilterConfig: BetriebskontaktAuswahlConfig = {
        showLandwirte: true,
        showHaendler: true,
        showSchlachthoefe: true,
        showAll: true,
        multiSelect: false,
        compact: true,
        placeholder: 'Betriebskontakt auswählen...',
        allowCreateNew: false,
        keepOpenOnSelect: false,
    };
    betriebskontaktCardConfig = {
        showActions: true,
        showMenu: false,
        showFavorit: false,
        showBadge: true,
        compact: false,
        selectedMode: true,
    };
    betriebskontakteFuerGruppe: BetriebskontaktMitAnzahl[] = [];
    isLoadingBetriebskontakte = false;

    constructor(
        public dialogRef: MatDialogRef<GruppeTierFilterDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: any,
        private readonly stammdatenService: StammdatenService,
    ) {
        if (data.filterSettings) {
            this.filterSettings = data.filterSettings.clone();
        }
        this.selectedTierFilterCtrl.setValue(this.filterSettings?.filter);
        this.gruppe = this.data?.gruppe;
        this.gruppeIds = (this.data?.gruppeIds || [])
            .map((id: unknown) => Number(id))
            .filter((id: number) => id > 0);
        this.disableTierFilterSelection = !!this.data?.disableTierFilterSelection;
        if (this.disableTierFilterSelection) {
            this.disabledTierFilterOptions = [
                TierFilter.TOTE_TIERE,
                TierFilter.LEBENDE_TIERE,
                TierFilter.LEBENDE_TIERE_NICHT_ABGEGANGEN,
                TierFilter.LEBENDE_TIERE_IN_AUSSTALLLISTE,
            ];
        }
        if (data?.info) {
            this.info = data?.info;
        }
        this.showEinstallgewichtInfo = !!data?.showEinstallgewichtInfo;
        this.showAbgangsgewichtInfo = !!data?.showAbgangsgewichtInfo;
        this.showSaveFilterOption = !!data?.showSaveFilterOption;
        if (this.showSaveFilterOption) {
            this.saveFilterCtrl.setValue(!!data?.saveFilterEnabled);
        }
    }

    ngOnInit(): void {
        this.selectedTierFilterCtrl.valueChanges.subscribe(tierFilter => {
            this.filterSettings.filter = tierFilter;
        });
        this.loadBetriebskontakteFuerGruppe();
    }

    setFilter(selected: TierFilter) {
        this.filterSettings.filter = selected?.key != null ? selected : TierFilter.ALLE_TIERE;
    }

    apply() {
        this.filterSettings.normalize();
        this.dialogRef.close(
            new TierFilterDialogResult(
                this.filterSettings,
                this.showSaveFilterOption && this.saveFilterCtrl.value === true,
            ),
        );
    }

    delete() {
        const empty = new TierFilterSettings();
        empty.showAbteile = this.filterSettings.showAbteile;
        empty.showReklamationen = this.filterSettings.showReklamationen;
        empty.showGeschlechter = this.filterSettings.showGeschlechter;
        empty.showRassen = this.filterSettings.showRassen;
        empty.showFilter = this.filterSettings.showFilter;
        empty.showAbgangslisten = this.filterSettings.showAbgangslisten;
        empty.showViehhaendler = this.filterSettings.showViehhaendler;
        empty.showEinstalldatum = this.filterSettings.showEinstalldatum;
        empty.showBetriebskontakte = this.filterSettings.showBetriebskontakte;
        empty.showHerkunftsbetriebe = this.filterSettings.showHerkunftsbetriebe;
        this.dialogRef.close(
            new TierFilterDialogResult(
                empty,
                this.showSaveFilterOption && this.saveFilterCtrl.value === true,
            ),
        );
    }

    viehhaendlerSelected(haendler: Haendler[]) {
        const next = haendler?.length ? haendler : [];
        if (this.sameHaendlerSelection(this.filterSettings.viehhaendler, next)) {
            return;
        }
        this.filterSettings.viehhaendler = next;
        if (this.filterSettings.viehhaendler.length > 0) {
            this.filterSettings.betriebskontakte = [];
        }
        this.filterEinstalldatum?.update();
        this.loadBetriebskontakteFuerGruppe();
    }

    einstalldatumSelected(einstalldaten: GruppeTierEinstalldatum[]) {
        const next = einstalldaten?.length ? einstalldaten : [];
        if (this.sameEinstalldatumSelection(this.filterSettings.einstalldatum, next)) {
            return;
        }
        this.filterSettings.einstalldatum = next;
        this.filterViehhaendler?.update();
        this.loadBetriebskontakteFuerGruppe();
    }

    betriebskontaktSelected(kontakt: Betriebskontakt) {
        if (kontakt) {
            const alreadySelected =
                this.filterSettings.betriebskontakte?.length === 1 &&
                Number(this.filterSettings.betriebskontakte[0]?.kunde_id) === Number(kontakt.kunde_id);
            if (alreadySelected) {
                return;
            }
            this.filterSettings.betriebskontakte = [kontakt];
            this.filterSettings.viehhaendler = [];
            this.filterViehhaendler?.setSelectedHaendler([]);
            this.filterEinstalldatum?.update();
        }
    }

    betriebskontaktEntfernen() {
        this.filterSettings.betriebskontakte = [];
        this.filterEinstalldatum.update();
        this.filterViehhaendler.update();
        this.loadBetriebskontakteFuerGruppe();
    }

    private kontaktBezeichnung(kontakt: BetriebskontaktMitAnzahl): string {
        const name = kontakt.bezeichnung || '';
        if (!this.showEinstallgewichtInfo) {
            return name;
        }
        return `${name} (${formatTierMengenLabel(kontakt)})`;
    }

    private sameEinstalldatumSelection(
        a: GruppeTierEinstalldatum[] | null | undefined,
        b: GruppeTierEinstalldatum[] | null | undefined,
    ): boolean {
        const left = a || [];
        const right = b || [];
        if (left.length !== right.length) {
            return false;
        }
        const keys = (items: GruppeTierEinstalldatum[]) =>
            items
                .map(item => {
                    const raw = item?.einstalldatum;
                    if (raw instanceof Date) {
                        return formatDate(raw, 'yyyy-MM-dd', 'de');
                    }
                    return raw ? String(raw).slice(0, 10) : '';
                })
                .filter(Boolean)
                .sort()
                .join('|');
        return keys(left) === keys(right);
    }

    private sameHaendlerSelection(a: Haendler[] | null | undefined, b: Haendler[] | null | undefined): boolean {
        const left = a || [];
        const right = b || [];
        if (left.length !== right.length) {
            return false;
        }
        const keys = (items: Haendler[]) =>
            items
                .map(item => Number(item?.viehhaendler_id || 0))
                .filter(id => id > 0)
                .sort((x, y) => x - y)
                .join('|');
        return keys(left) === keys(right);
    }

    private loadBetriebskontakteFuerGruppe() {
        const targetGroupIds =
            this.gruppeIds.length > 0
                ? this.gruppeIds
                : this.gruppe?.gruppe_id
                  ? [Number(this.gruppe.gruppe_id)]
                  : [];
        if (targetGroupIds.length === 0) {
            this.betriebskontakteFuerGruppe = [];
            return;
        }

        let params = new HttpParams();
        if (this.filterSettings?.einstalldatum?.length > 0) {
            for (const datum of this.filterSettings.einstalldatum) {
                params = params.append(
                    'einstalldatum[]',
                    formatDate(datum.einstalldatum, 'yyyy-MM-dd', 'de', 'Europe/Berlin'),
                );
            }
        }

        if (this.filterSettings?.viehhaendler?.length > 0) {
            for (const h of this.filterSettings.viehhaendler) {
                params = params.append('viehhaendler_id[]', h.viehhaendler_id.toString());
            }
        }
        if (this.showEinstallgewichtInfo) {
            params = params.set('mit_einstallgewicht', '1');
        }

        this.isLoadingBetriebskontakte = true;
        const requests = targetGroupIds.map(gruppeId =>
            this.stammdatenService.ladeBetriebskontakteFuerGruppe(gruppeId, params),
        );

        forkJoin(requests).subscribe({
                next: result => {
                    this.isLoadingBetriebskontakte = false;
                    const byKundeId = new Map<number, BetriebskontaktMitAnzahl>();
                    for (const res of result || []) {
                        for (const rec of res?.records || []) {
                            const kundeId = Number(rec?.kunde_id || 0);
                            if (kundeId <= 0) {
                                continue;
                            }
                            const existing = byKundeId.get(kundeId);
                            const anzahl = Number(rec?.anzahl || 0);
                            if (existing) {
                                existing.anzahl = Number(existing.anzahl || 0) + anzahl;
                                if (rec?.anzahl_mit_gewicht != null || existing.anzahl_mit_gewicht != null) {
                                    existing.anzahl_mit_gewicht =
                                        Number(existing.anzahl_mit_gewicht || 0) + Number(rec?.anzahl_mit_gewicht || 0);
                                    existing.gesamtgewicht =
                                        Number(existing.gesamtgewicht || 0) + Number(rec?.gesamtgewicht || 0);
                                    existing.bezeichnung_final = this.kontaktBezeichnung(existing);
                                }
                                continue;
                            }
                            const kontakt: BetriebskontaktMitAnzahl = {
                                kontakt_id: kundeId,
                                benutzer_id: 0,
                                kunde_id: kundeId,
                                favorit: 0,
                                ist_kunde: 0,
                                bezeichnung: rec.bezeichnung,
                                bezeichnung_final: rec.bezeichnung,
                                vvvo: rec.vvvo,
                                email: rec.email,
                                anzahl,
                                anzahl_mit_gewicht: rec?.anzahl_mit_gewicht,
                                gesamtgewicht: rec?.gesamtgewicht,
                            };
                            kontakt.bezeichnung_final = this.kontaktBezeichnung(kontakt);
                            byKundeId.set(kundeId, kontakt);
                        }
                    }
                    this.betriebskontakteFuerGruppe = Array.from(byKundeId.values());
                },
                error: err => {
                    console.error('Fehler beim Laden der Betriebskontakte', err);
                    this.isLoadingBetriebskontakte = false;
                    this.betriebskontakteFuerGruppe = [];
                },
            });
    }
}
